/**
 * Overview module — mock widget loaders: the four `Size=sm` figures
 * (prototype `views/cards/SummaryKpiCards.tsx`).
 */
import type { DashboardQuery, WidgetLoaders } from '@/data-sources/contract';
import type { MetricSummaryData } from '@/data-sources/contract/widgets/overview';

import { USER_BRAND } from '../engine/brand';
import type { MentionRow } from '../engine/generator';
import { metrics, type MetricId } from '../engine/metrics';
import { scoped } from '../query';

/** `MetricSummaryCard`: one metric over the user's brand rows, with the comparison figure. */
function metricSummary(query: DashboardQuery, metric: MetricId): MetricSummaryData {
  const s = scoped(query);
  return {
    value: {
      current: metrics[metric].reduce(s.own),
      previous: s.ownPrevious ? metrics[metric].reduce(s.ownPrevious) : null,
    },
    mentions: s.own.length,
  };
}

/** The two sides of `standingsFor(rows).find((s) => s.own).share`. */
const shareCounts = (rows: readonly MentionRow[]) => ({
  own: rows.filter((row) => row.brand === USER_BRAND).length,
  category: rows.length,
});

export const overviewWidgetLoaders = {
  'total-mentions': async ({ query }) => metricSummary(query, 'mentions'),
  'total-unique-audience': async ({ query }) => metricSummary(query, 'reach'),
  'negative-conversation': async ({ query }) => metricSummary(query, 'negativeMentions'),

  'share-of-voice-figure': async ({ query, params }) => {
    /*
     * `all` means every operator tracked: a brand selection cannot narrow the
     * denominator. Overview (the only `all` caller) draws no Brands chip, so
     * its query carries no brand selection anyway — this states the rule
     * rather than relying on it.
     */
    const effective: DashboardQuery =
      params.category === 'all'
        ? {
            ...query,
            selections: Object.fromEntries(
              Object.entries(query.selections).filter(([key]) => key !== 'brand'),
            ),
          }
        : query;
    const s = scoped(effective);
    return {
      current: shareCounts(s.category),
      previous: s.categoryPrevious ? shareCounts(s.categoryPrevious) : null,
    };
  },
} satisfies Partial<WidgetLoaders>;
